import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useForm, UseFormReturn } from 'react-hook-form';
import { TextInputController } from '../InputControllers/TextInputController';
import { SelectController } from '../InputControllers/SelectController';
import { validateEmail } from '../../helpers/validateEmail';
import { apiOrganization } from '../../resources/api/apiOrganization';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { NotificationContext } from '../../contexts/NotificationContext';
import { EOrganizationRole, organizationRoles } from '../../const/roles';
import { useTranslation } from 'react-i18next';
import { IBasicOrganization } from '../../resources/models/IOrganization';
import Grid from '@mui/material/Grid';
import Launch from '@mui/icons-material/Launch';
import { styled } from '@mui/system';
import { config } from '../../config';
import Box from '@mui/system/Box';

interface IProps {
  open: boolean;
  closeDialog: () => void;
  organization: IBasicOrganization;
  onDone?: () => Promise<any>;
}

interface IFormData {
  emails: string;
  role_id: string;
}

const parseEmails = (emailsString: string) => {
  return emailsString
    .split(',')
    .map((single) => single.trim())
    .filter((single) => single);
};

const { useInviteUsers } = apiOrganization;

const roleLabels: Record<EOrganizationRole, string> = {
  [EOrganizationRole.Admin]: 'roles.organization.admin',
  [EOrganizationRole.Member]: 'roles.organization.member',
};

const LinkContainer = styled('div')({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: '1rem',
});

const StyledLaunch = styled(Launch)({
  marginLeft: '8px',
});

export const RoleSelectionPartial: FC<{ form: UseFormReturn<any> }> = ({ form }) => {
  const { t } = useTranslation();

  const roleItems = useMemo(
    () =>
      organizationRoles.map((role) => ({
        value: role.id,
        label: t(roleLabels[role.role]),
      })),
    [t]
  );

  return (
    <>
      <Grid item xs={7}>
        <SelectController
          control={form.control}
          errors={form.formState.errors}
          label={t('inviteUser.role.label')}
          name="role_id"
          items={roleItems}
          rules={{ required: true }}
        />
      </Grid>
      <Grid item xs={5}>
        <LinkContainer>
          <Button
            href={config.rolesHelpPage}
            variant={'text'}
            size={'small'}
            target={'_blank'}
            rel={'noopener noreferrer'}
          >
            {t('inviteUser.rolesAndPermissionsLink')}
            <StyledLaunch fontSize={'small'} />
          </Button>
        </LinkContainer>
      </Grid>
    </>
  );
};

export const InviteUserModal: FC<IProps> = ({ open, closeDialog, organization, onDone }) => {
  const { t } = useTranslation();

  const form = useForm<IFormData>();

  const { execute } = useInviteUsers(organization?.name);

  const handleApiError = useApiErrorHandler();
  const { addNotification } = useContext(NotificationContext);

  useEffect(() => {
    if (open) {
      form.reset({
        role_id: '',
        emails: '',
      });
    }
  }, [open]);

  const onSubmit = useCallback(
    async (data: IFormData) => {
      try {
        const response = await execute(parseEmails(data.emails), data.role_id);
        if (response.status_code === 207) {
          addNotification({
            message: response.message,
            severity: 'success',
          });
          const errors = (response.payload || []).filter(({ status_code }) => status_code >= 400);

          for (const err of errors) {
            addNotification(
              {
                message: err.message,
                severity: 'error',
              },
              false
            );
          }
        } else {
          addNotification({
            message: t('inviteUser.success'),
            severity: 'success',
          });
        }
        await onDone?.();
        closeDialog();
      } catch (e) {
        handleApiError(e);
      }
    },
    [execute]
  );

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>Invite user</DialogTitle>
      <DialogContent>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextInputController
                control={form.control}
                errors={form.formState.errors}
                label={t('inviteUser.email.label')}
                name={'emails'}
                rules={{
                  required: true,
                  validate: (value) => {
                    const emails = parseEmails(value);

                    const invalid = emails.find((email) => !validateEmail(email));

                    return invalid ? (t('inviteUser.email.invalid', { email: invalid }) as string) : true;
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box mt={1} mb={-1}>
                {t('inviteUser.descriptionMultiple')}
              </Box>
            </Grid>

            <RoleSelectionPartial form={form} />

            <Grid item xs={12}>
              {t('inviteUser.explanation')}
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>{t('inviteUser.cancel')}</Button>
        <Button variant="contained" onClick={form.handleSubmit(onSubmit)}>
          {t('inviteUser.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
